import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Scissors, Copy, Loader2, Clock, Info, Upload, Video, Mic, MessageSquare, Crown, Layout, Lightbulb, FileVideo } from 'lucide-react';
import { copyToClipboard } from '../utils/clipboard';

const PERSIST_KEY = 'creatorflow_editor';
const PERSIST_MINUTES = 5;

const Editor = () => {
  const { api, user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(() => {
    try {
      const saved = localStorage.getItem(PERSIST_KEY);
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        if (Date.now() - timestamp < PERSIST_MINUTES * 60 * 1000) return data;
        localStorage.removeItem(PERSIST_KEY);
      }
    } catch (e) {}
    return null;
  });
  const [form, setForm] = useState({
    script: location.state?.script || '',
    platform: 'tiktok',
    silentMode: false,
    editingStyle: 'tiktok_viral',
    images: []
  });

  // Persist result when it changes
  useEffect(() => {
    if (result) {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({ data: result, timestamp: Date.now() }));
    }
  }, [result]);

  const hasAccess = user?.subscriptionPlan === 'pro' || user?.subscriptionPlan === 'max';

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.script) {
      toast.error('Please enter a script');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/editor/generate', form);
      setResult(res.data);
      toast.success(`Editing plan created! (${res.data.creditsUsed} credits used)`);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate editing plan');
    } finally {
      setLoading(false);
    }
  };

  const copyFullPlan = () => {
    if (!result) return;
    const text = result.scenes.map(scene => 
      `[${scene.timeRange}] ${scene.shotType}\nNarration: ${scene.narration}\nOn-screen: ${scene.onScreenText}\nEditing: ${scene.editingNotes}\nTip: ${scene.creatorTip}`
    ).join('\n\n---\n\n');
    copyToClipboard(text);
    toast.success('Full plan copied!');
  };

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="editor-locked">
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Editor
            </h1>
            <div className="flex items-center gap-2 px-4 py-2 bg-violet-500 rounded-xl">
              <Crown className="w-5 h-5 text-white" />
              <span className="font-bold text-white">PRO</span>
            </div>
          </div>
          <p className="text-slate-400 mt-3 text-lg">Transform scripts into actionable editing instructions</p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center">
                <Scissors className="w-8 h-8 text-violet-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Upgrade to Access Editor</h2>
              <p className="text-slate-400 max-w-md mx-auto">
                The Editor feature is available on Pro and Max plans. Get AI-generated editing plans with scene breakdowns, shot types, and creator tips.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-4 h-4 text-violet-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Auto Cut Map</p>
                  <p className="text-slate-500 text-xs">Automatic timeline with hook, beats and CTA breakdown</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Layout className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Scene Breakdown</p>
                  <p className="text-slate-500 text-xs">Shot-by-shot instructions for every scene</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Lightbulb className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Creator Tips</p>
                  <p className="text-slate-500 text-xs">Pro editing tips for each scene</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FileVideo className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">CapCut & Premiere Ready</p>
                  <p className="text-slate-500 text-xs">Export-ready editing plan for any editor</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button 
                onClick={() => navigate('/billing')}
                className="bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white"
                data-testid="editor-upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro Creator - $19/month
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="editor">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">Editor</h1>
          <p className="text-slate-400 text-lg">Transform scripts into actionable editing instructions.</p>
        </div>
        {result && (
          <div className="flex items-center gap-3">
            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 px-3 py-1">
              <Clock className="w-3 h-3 mr-1" /> 30s total
            </Badge>
            <Badge className="bg-slate-800 text-white border-slate-700 px-3 py-1">
              TikTok Viral
            </Badge>
            <Button
              onClick={copyFullPlan}
              variant="outline"
              className="border-slate-700 text-white hover:bg-slate-800"
              data-testid="copy-full-plan"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Full Plan
            </Button>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-slate-400 text-sm">
          This tool generates <span className="text-violet-400">editing instructions</span>, not finished videos. Follow these steps in CapCut or Premiere to finish your content.
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <Card className="bg-slate-800/30 border-slate-700/50 h-fit">
          <CardContent className="p-6">
            <form onSubmit={handleGenerate} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-white font-medium">Editing Style Preset</Label>
                <Select 
                  value={form.editingStyle} 
                  onValueChange={(v) => setForm({...form, editingStyle: v})}
                >
                  <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="tiktok_viral">TikTok Viral</SelectItem>
                    <SelectItem value="cinematic">Cinematic</SelectItem>
                    <SelectItem value="fast_paced">Fast Paced</SelectItem>
                    <SelectItem value="minimal">Minimal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white font-medium">Script</Label>
                <Textarea
                  data-testid="editor-script"
                  placeholder="Paste your script here..."
                  value={form.script}
                  onChange={(e) => setForm({...form, script: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 min-h-[150px] resize-y"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white font-medium">Platform</Label>
                <Select 
                  value={form.platform} 
                  onValueChange={(v) => setForm({...form, platform: v})}
                >
                  <SelectTrigger data-testid="editor-platform" className="bg-slate-900/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="reels">Instagram Reels</SelectItem>
                    <SelectItem value="shorts">YouTube Shorts</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="image-upload" className="text-white font-medium">Upload Images (Optional)</Label>
                <label
                  htmlFor="image-upload"
                  className="flex items-center justify-center w-full h-10 px-4 border border-slate-700 rounded-md text-slate-300 hover:bg-slate-800 cursor-pointer transition-colors"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {form.images?.length > 0 ? `${form.images.length} image(s) selected` : 'Upload Images'}
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  multiple
                  className="hidden"
                  onChange={(e) => setForm({...form, images: Array.from(e.target.files || [])})}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                <div>
                  <Label className="text-white font-medium">Silent Edit Mode</Label>
                  <p className="text-sm text-slate-500">Optimize for silent viewing</p>
                </div>
                <Switch
                  data-testid="silent-mode"
                  checked={form.silentMode}
                  onCheckedChange={(v) => setForm({...form, silentMode: v})}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 bg-violet-500 hover:bg-violet-600 text-white text-lg font-medium"
                data-testid="generate-editor-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Scissors className="w-5 h-5 mr-2" />
                    Generate Editing Plan
                  </>
                )}
              </Button>
              <p className="text-center text-slate-500 text-sm">Uses 5 credits</p>
            </form>
          </CardContent>
        </Card>

        {/* Right Column - Results */}
        <div className="space-y-6">
          {!result ? (
            <Card className="bg-slate-800/30 border-slate-700/50 min-h-[500px]">
              <CardContent className="p-6 h-full flex flex-col items-center justify-center text-center">
                <Scissors className="w-16 h-16 text-slate-600 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No editing plan yet</h3>
                <p className="text-slate-400">
                  Paste a script and generate your step-by-step guidance.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Timeline */}
              <Card className="bg-slate-800/30 border-slate-700/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Scissors className="w-5 h-5 text-violet-400" />
                    <h3 className="text-lg font-bold text-white">Auto Cut Map (Timeline)</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {result.timeline?.map((item, idx) => (
                      <div 
                        key={idx}
                        className="bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 text-center"
                      >
                        <p className="text-slate-500 text-xs mb-1">{item.timeRange}</p>
                        <p className="text-white font-medium">{item.section}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Scene Breakdown */}
              <Card className="bg-slate-800/30 border-slate-700/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Video className="w-5 h-5 text-violet-400" />
                    <h3 className="text-lg font-bold text-white">Actionable Scene Breakdown</h3>
                  </div>

                  <div className="space-y-6">
                    {result.scenes?.map((scene, idx) => (
                      <div key={idx} className="space-y-4" data-testid="editor-scene">
                        {/* Scene Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                              Scene {idx + 1}
                            </Badge>
                            <Badge className="bg-slate-800 text-white border-slate-700">
                              {scene.timeRange?.split('-')[0] === '0' ? 'Hook' : 
                               idx === result.scenes.length - 1 ? 'CTA' : 'Context'}
                            </Badge>
                            <span className="text-slate-500 text-sm">{scene.timeRange?.split('-')[1] || '2s'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white h-8 w-8">
                              <Mic className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white h-8 w-8">
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white h-8 w-8">
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Left - Shot Type & Instructions */}
                          <div className="space-y-3">
                            <div>
                              <p className="text-slate-500 text-xs uppercase mb-1">Shot Type</p>
                              <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <p className="text-white flex items-center gap-2">
                                  <Video className="w-4 h-4 text-slate-400" />
                                  {scene.shotType}
                                </p>
                              </div>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs uppercase mb-1">Instructions</p>
                              <ul className="space-y-2 text-sm text-slate-300">
                                {scene.editingNotes?.split('.').filter(s => s.trim()).slice(0, 3).map((note, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-violet-400">→</span>
                                    {note.trim()}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* Right - On-Screen Text & Creator Tip */}
                          <div className="space-y-3">
                            <div>
                              <p className="text-slate-500 text-xs uppercase mb-1">On-Screen Text</p>
                              <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <p className="text-white font-medium">{scene.onScreenText || 'None'}</p>
                              </div>
                            </div>
                            <div className="bg-cyan-900/20 rounded-lg p-3 border border-cyan-500/30">
                              <p className="text-cyan-400 text-xs uppercase mb-1">Creator Tip</p>
                              <p className="text-slate-300 text-sm italic">&ldquo;{scene.creatorTip}&rdquo;</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Editor;
